/**
 * `host.logData` `awaitConfirm` — the HONEST WRITE CONFIRMATION ladder.
 *
 * The other half of `HostAdapter.jsx` (it lives in its own module only because
 * a `.jsx` file that exports a component may not also export functions).
 *
 * `logData` is fire-and-forget: the widget hands a row to the host and never
 * learns whether it landed. Every blueprint that wants to say "Saved" therefore
 * has to hand-roll the same three-state ladder — flash pending, watch the next
 * pushed feed for the value, and give up with an honest line after a while —
 * and nutritionist's goal editor is the widget that made that obvious (prod
 * disables its submit, says *Saving…*, flips to *Saved* only when the feed
 * comes back carrying the new target, and after 8s says
 * *Couldn't confirm the save* and re-enables).
 *
 * So the ladder lives HERE, once, and a blueprint only declares it:
 *
 *   { "to": "host.logData",
 *     "payload": { "payload": {...},
 *                  "awaitConfirm": { "path": "entries[].payload.calorie_target",
 *                                    "timeout_ms": 8000 } } }
 *
 * `path` is read against the PUSHED FEED (the same object `host.dataReceived`
 * carries), never against the widget's own optimistic state — that is the whole
 * point: an optimistic echo is not a confirmation.
 *
 * The Flutter mirror is `ViewIrAwaitConfirm` in `view_ir/host_adapter.dart`;
 * the two must agree about WHEN "Saved" is honest, not just about the words.
 */
import { getPath } from '../paths';

export const AWAIT_CONFIRM_DEFAULTS = Object.freeze({
  timeout_ms: 8000,
  pendingLabel: 'Saving…',
  successLabel: 'Saved',
  timeoutLabel: "Couldn't confirm the save",
});

/** PRESENT — the kit's "this field has a value" (`0` and `false` do). */
function present(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

function label(v, fallback) {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/**
 * Normalise a declared `awaitConfirm` into the full spec, or null when the
 * blueprint declared nothing usable.
 *
 * A confirmation with no `path` has nothing to look for, so it is NOT a
 * confirmation — it degrades to plain fire-and-forget rather than to a spinner
 * that can only ever time out.
 */
export function normalizeAwaitConfirm(spec) {
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return null;
  const path = typeof spec.path === 'string' ? spec.path.trim() : '';
  if (!path) return null;
  const ms = Number(spec.timeout_ms);
  return {
    path,
    timeout_ms: Number.isFinite(ms) && ms > 0 ? ms : AWAIT_CONFIRM_DEFAULTS.timeout_ms,
    pendingLabel: label(spec.pendingLabel, AWAIT_CONFIRM_DEFAULTS.pendingLabel),
    successLabel: label(spec.successLabel, AWAIT_CONFIRM_DEFAULTS.successLabel),
    timeoutLabel: label(spec.timeoutLabel, AWAIT_CONFIRM_DEFAULTS.timeoutLabel),
  };
}

/**
 * Split a `host.logData` payload into what the HOST sees and what the LADDER
 * keeps. The host bridge's contract is untouched — `awaitConfirm` is kit
 * vocabulary and never reaches the app callback.
 */
export function splitAwaitConfirm(payload) {
  const confirm = normalizeAwaitConfirm(payload?.awaitConfirm);
  if (!confirm) return { payload, confirm: null };
  const rest = { ...payload };
  delete rest.awaitConfirm;
  return { payload: rest, confirm };
}

/**
 * The value this write is waiting to see come back.
 *
 * `path` addresses the FEED (`entries[].payload.calorie_target`), so it rarely
 * resolves against the write itself; the write carries the same value under the
 * path's LEAF (`calorie_target`), one level inside `payload` for the usual
 * `{kind, payload:{…}}` envelope. When neither answers we cannot compare
 * values and fall back to PRESENCE, which is still stricter than not checking.
 */
function expectedValue(payload, path) {
  const direct = getPath(payload, path);
  if (present(direct) && !Array.isArray(direct)) return direct;
  const leaf = String(path).split('.').filter(Boolean).pop().replace(/\[\d*\]$/, '');
  for (const src of [payload?.payload, payload]) {
    const v = getPath(src, leaf);
    if (present(v)) return v;
  }
  return undefined;
}

/** The feed a `host.dataReceived` payload wraps, or the feed itself. */
function feedRoot(observed) {
  if (!observed || typeof observed !== 'object') return null;
  const inner = observed.feed;
  return inner && typeof inner === 'object' ? inner : observed;
}

/** True when the pushed feed carries `expected` (or anything, when unknown). */
function feedCarries(feed, path, expected) {
  const read = getPath(feed, path);
  const flat = (Array.isArray(read) ? read : [read]).flat(2);
  if (expected === undefined) return flat.some(present);
  return flat.some((v) => present(v) && String(v) === String(expected));
}

/**
 * The ladder itself. Timers are injected so a test can drive it without a
 * clock, and so the Flutter mirror can take the same shape.
 *
 *   const confirm = createAwaitConfirm({ onStatus: setWriteStatus });
 *   const { payload, status } = confirm.arm(delivered);   // status = pending
 *   host.logData(payload);                                // host sees no awaitConfirm
 *   …later, on every feed push:
 *   confirm.observeFeed(feed);                            // → confirmed, or → timeout
 */
export function createAwaitConfirm({
  onStatus,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  let armed = null;

  const emit = (status) => {
    if (status) onStatus?.(status);
    return status;
  };

  const disarm = () => {
    if (armed?.timer !== undefined) clearTimer(armed.timer);
    armed = null;
  };

  const settle = (state) => {
    if (!armed) return null;
    const { spec } = armed;
    disarm();
    return emit({
      state,
      label: state === 'confirmed' ? spec.successLabel : spec.timeoutLabel,
      path: spec.path,
    });
  };

  return {
    /**
     * Take a `host.logData` payload; return what to hand the host plus the
     * `pending` status (null when the blueprint declared no `awaitConfirm`, in
     * which case the payload passes through byte-identical).
     *
     * A second write supersedes the first: the older ladder is cancelled
     * silently rather than racing the newer one to a label.
     */
    arm(delivered) {
      const { payload, confirm } = splitAwaitConfirm(delivered);
      if (!confirm) return { payload, status: null };
      disarm();
      const expected = expectedValue(delivered, confirm.path);
      armed = { spec: confirm, expected, timer: undefined };
      armed.timer = setTimer(() => settle('timeout'), confirm.timeout_ms);
      return {
        payload,
        status: emit({ state: 'pending', label: confirm.pendingLabel, path: confirm.path }),
      };
    },

    /**
     * A pushed feed arrived (the `host.dataReceived` payload, or a bare feed).
     * Returns the `confirmed` status when this feed is the one that proves the
     * write, otherwise null — a feed that does not carry it is not a failure,
     * it is simply not yet the answer.
     */
    observeFeed(observed) {
      if (!armed) return null;
      const feed = feedRoot(observed);
      if (!feed) return null;
      if (!feedCarries(feed, armed.spec.path, armed.expected)) return null;
      return settle('confirmed');
    },

    /** Give up on the in-flight write without saying anything (page left). */
    cancel() { disarm(); },
    dispose() { disarm(); },

    /** What is in flight, for a host that wants to render it itself. */
    get pending() {
      return armed ? { path: armed.spec.path, label: armed.spec.pendingLabel } : null;
    },
  };
}
